import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Lays a row's children out on one line while they fit, and stacks them when
 * they don't, so a long app name never breaks inside a link. `content` names
 * what the row shows (its labels): when it changes, the row tries one line
 * again.
 *
 * Measures before paint while the row is on one line, so the row must keep
 * its children at their full width (no wrapping, no shrinking) until it is
 * `stacked`.
 */
export function useStackWhenTooWide<T extends HTMLElement>(content: string) {
  const ref = useRef<T>(null);
  const [stacked, setStacked] = useState<{
    content: string;
    width: number;
  } | null>(null);
  const isStacked = stacked?.content === content;

  useLayoutEffect(() => {
    const row = ref.current;
    if (!row) return;
    const check = () => {
      if (isStacked) {
        // Back to one line once the row is wide enough again.
        if (stacked && row.clientWidth >= stacked.width) setStacked(null);
        return;
      }
      const width = oneLineWidth(row);
      if (width > row.clientWidth) setStacked({ content, width });
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(row);
    // A link widens without the row (a web font arriving).
    for (const child of Array.from(row.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [content, isStacked, stacked]);

  return { ref, stacked: isStacked };
}

/** The children's full widths plus the gaps between them. */
function oneLineWidth(row: HTMLElement): number {
  const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
  const children = Array.from(row.children);
  const widths = children.reduce(
    (sum, child) => sum + child.getBoundingClientRect().width,
    0,
  );
  return widths + gap * Math.max(0, children.length - 1);
}
